import { Box, Grid, MenuItem } from "@mui/material";
import { isEmpty } from "lodash";
import React, { useEffect, useMemo, useState } from "react";
import axiosClient from "../../common/AxiosClient";
import { AttributeActionMenu } from "../../components/AttributeActionMenu";
import { Button } from "../../components/Button";
import { Dialog } from "../../components/dialogs/Dialog";
import { Placeholder } from "../../components/Placeholder";
import { SectionLoader } from "../../components/SectionLoader";
import { MultipleFilter } from "../../components/selectors/MultipleFilter";
import { ProfileTrainingTable } from "../../components/table/ProfileTrainingTable";
import {
  EmployeeTrainingStatus,
  IntegrationSource,
  statusLabels,
  TRAINING_ATTRIBUTE_DIALOG_TYPE,
  Training_Dialog_Mode
} from "../../constants";
import { useAppQuery } from "../../hooks/useAppQuery";
import { useCombineLoading } from "../../hooks/useCombineLoading";
import useLoadPratical from "../../hooks/useEmployeePraticals";
import useLoadTrainings from "../../hooks/useEmployeeTraining";
import { IPracticalTraining } from "../../interfaces/PracticalTraining";
import { IGetEmployeeTraining } from "../../interfaces/Training";
import { ReactComponent as AddIcon } from "../../resources/images/icons-add.svg";
import { ReactComponent as CertificateIcon } from "../../resources/images/icons-certificate.svg";
import { ReactComponent as PendingIcon } from "../../resources/images/icons-pending.svg";
import { PracticalConfirmationDialog } from "../practicals/PracticalConfirmationDialog";
import AddEditEmployeeTrainingDialog from "./AddEditEmployeeTrainingDialog";
import { ProfileTrainingCompetenciesDialog } from "./ProfileTrainingCompeteniesDialog";

interface ProfileTrainingSectionProps {
  theme: any;
  organization?: any;
  employee: any;
  profile?: any;
  isInstructorViewing?: boolean;
  readOnly?: boolean;
  user?: any;
  groupId?: string;
}
const filters = [
  { key: "training", label: "Training" },
  { key: "practical", label: "Practical" },
  { key: "assessment", label: "Assessment" }
];

export const ProfileTrainingSection: React.FC<ProfileTrainingSectionProps> = (props) => {
  const { theme, employee, profile, isInstructorViewing, readOnly, user, groupId } = props;

  const [state, setState] = useState({
    associatedAttributeDetail: null,
    isTrainingAttributesDialogShow: false,
    trainingDialogType: null,
    sortModel: [{ field: "completedDate", sort: "desc" }],
    combinedRows: [] as IPracticalTraining[],
    filteredRows: [],
    isPracticalConfirmationDialogShow: false,
    selectedPractical: null,
    isOpenAddTrainingDialog: false,
    isLoading: false,
    selectedFilters: [],
    isConfirmPracticalLoading: false,
    trainingDialogMode: Training_Dialog_Mode.add,
    selectedTraining: null,
    showAddDisabledNotice: false
  });
  //
  const { data, isFetching } = useAppQuery<IGetEmployeeTraining>("/EmployeeTraining/GetTrainings");

  const trainingOptions = useMemo(() => {
    if (!data) return [];

    return data.trainings.map((training) => ({
      id: training.id,
      name: training.organizationIntegrationAbbreviation
        ? `${training.courseName} | ${training.organizationIntegrationAbbreviation}`
        : training.courseName
    }));
  }, [data]);

  const {
    trainings,
    isLoading: isTrainingLoading,
    refetch: onLoadTrainings
  } = useLoadTrainings({
    params: { employeeId: employee.employeeId },
    enabled: !!employee.employeeId
  });

  const {
    practicals,
    isLoading: isPracticalLoading,
    refetch: onLoadPraticals
  } = useLoadPratical({
    params: {
      employeeId: employee.employeeId,
      pageNumber: 1,
      pageSize: 1000
    },
    enabled: !!employee.employeeId
  });
  const [menu, setMenu] = useState<any>(null);
  const [actionTarget, setActionTarget] = useState<any>(null);
  const [actionAttribute, setActionAttribute] = useState<any>(null);

  const isLoading = useCombineLoading([isTrainingLoading, isPracticalLoading]);

  const setMenuRef = (menuRef: any) => {
    setMenu(menuRef);
  };
  const reloadData = async () => {
    try {
      setState((prev) => ({ ...prev, isLoading: true }));

      await Promise.all([onLoadTrainings(), onLoadPraticals()]);
    } catch (error) {
    } finally {
      setState((prev) => ({ ...prev, isLoading: false }));
    }
  };

  useEffect(() => {
    if (trainings && practicals) {
      onCombineData(trainings, practicals);
    }
  }, [trainings, practicals]);

  const formattedTrainings = async (trainings: []) => {
    if (isEmpty(trainings)) return [];

    return trainings.map((training: any) => ({
      id: training.id,
      name: training.organizationIntegrationAbbreviation
        ? `${training.courseName} | ${training.organizationIntegrationAbbreviation}`
        : training.courseName,
      completedDate:
        training.employeeTrainingValidationStatus === EmployeeTrainingStatus.Approved
          ? training.completedDate
          : training.validationRequest.completionDate,
      status: statusLabels[training.employeeTrainingValidationStatus as keyof typeof statusLabels],
      statusValue: training.employeeTrainingValidationStatus,
      isTraining: true,
      type: "Training",
      integrationCourseId: training.integrationCourseId,
      employeeTrainingValidationRequestId: training.employeeTrainingValidationRequestId,
      organizationIntegrationIntegrationSource: training.organizationIntegrationIntegrationSource
    }));
  };

  const formattedPracticals = async (practicals: []) => {
    if (isEmpty(practicals)) return [];

    return practicals.map((practical: any) => ({
      id: practical.id,
      name: practical.name,
      completedDate: practical.practicalDate,
      practicalDate: practical.practicalDate,
      status: practical.status,
      isAssessment: practical.isAssessment,
      isPractical: true,
      instructorFullname: practical.instructorFullname,
      acknowledgedDate: practical.acknowledgedDate,
      description: practical.description,
      practicalAttributeNames: practical.practicalAttributeNames,
      practicalTypeName: practical.practicalTypeName,
      isAcknowledged: practical.isAcknowledged,
      type: practical.isAssessment ? "Assessment" : "Practical"
    }));
  };

  const onCombineData = async (trainings: [], practicals: []) => {
    const [formattedTrainingsList, formattedPracticalsList] = await Promise.all([
      formattedTrainings(trainings),
      formattedPracticals(practicals)
    ]);

    const rows = [...formattedTrainingsList, ...formattedPracticalsList];

    setState((prev) => ({
      ...prev,
      combinedRows: rows
    }));

    onFilterData(rows);
  };

  const onShowTrainingAttributesDialog = (associatedRow: any) => {
    setState((prev: any) => ({
      ...prev,
      associatedAttributeDetail: associatedRow,
      isTrainingAttributesDialogShow: true,
      trainingDialogType: TRAINING_ATTRIBUTE_DIALOG_TYPE.DETAIL
    }));
  };

  const onCloseTrainingAttributesDialog = () => {
    setState((prev) => ({
      ...prev,
      isTrainingAttributesDialogShow: false,
      trainingDialogType: null
    }));
  };

  const isSelfView = () => {
    return user?.employeeId === employee?.employeeId;
  };

  const onFilterTypeChange = (e: any, selected: any) => {
    setState((prev) => ({ ...prev, selectedFilters: selected }));
    onFilterData(state.combinedRows, selected);
  };

  const onFilterData = (combinedRows: IPracticalTraining[], selectedFilters?: any[]) => {
    const filterToUse = selectedFilters || state.selectedFilters;

    if (isEmpty(filterToUse)) {
      setState((prev: any) => ({ ...prev, filteredRows: combinedRows }));
      return;
    }

    const filterKeys = filterToUse.map((f: any) => f.label.toLowerCase());

    const filteredData = combinedRows.filter((row: any) =>
      filterKeys.includes(row.type.toLowerCase())
    );

    setState((prev: any) => ({ ...prev, filteredRows: filteredData }));
  };

  const onRowSelect = (e: any, row: any) => {
    if (row && row.isPractical) {
      return onShowPracticalConfirmationDialog(row);
    }
    if (
      row &&
      row.isTraining &&
      row.organizationIntegrationIntegrationSource !== IntegrationSource.Manual
    ) {
      return;
    }
    if (isInstructorViewing && !isSelfView()) return onEditTraining(row);

    if (
      (!isInstructorViewing && !isSelfView()) ||
      [EmployeeTrainingStatus.PendingApproval, EmployeeTrainingStatus.Approved].includes(
        row.statusValue
      )
    ) {
      return setState((prev) => ({
        ...prev,
        trainingDialogMode: Training_Dialog_Mode.details,
        selectedTraining: row,
        isOpenAddTrainingDialog: true
      }));
    }

    onFeedbackAction(row);
  };

  const onShowPracticalConfirmationDialog = (row: any) => {
    setState((prev) => ({
      ...prev,
      isPracticalConfirmationDialogShow: true,
      selectedPractical: row
    }));
  };

  const onClosePracticalConfirmationDialog = () => {
    setState((prev) => ({
      ...prev,
      isPracticalConfirmationDialogShow: false,
      selectedPractical: null
    }));
  };

  const onConfirmPractical = (practical: any) => {
    setState((prev) => ({ ...prev, isConfirmPracticalLoading: true }));
    axiosClient
      .put(`/Practical/${practical.id}/acknowledge`)
      .then(() => {
        onClosePracticalConfirmationDialog();
        reloadData();
      })
      .finally(() => {
        setState((prev) => ({ ...prev, isConfirmPracticalLoading: false }));
      });
  };

  const onOpenAddTrainingDialog = () => {
    if (!employee?.hasJob || !profile?.hasApprover) {
      setState((prev) => ({ ...prev, showAddDisabledNotice: true }));
      return;
    }

    setState((prev) => ({
      ...prev,
      isOpenAddTrainingDialog: true,
      trainingDialogMode: Training_Dialog_Mode.add
    }));
  };

  const onCloseAddTrainingDialog = () => {
    setState((prev) => ({
      ...prev,
      isOpenAddTrainingDialog: false,
      trainingDialogMode: Training_Dialog_Mode.add,
      selectedTraining: null
    }));
  };

  const onFeedbackAction = (row: any) => {
    setState((prev) => ({
      ...prev,
      trainingDialogMode: Training_Dialog_Mode.feedback,
      selectedTraining: row,
      isOpenAddTrainingDialog: true
    }));
  };

  const onEditTraining = (row: any) => {
    setState((prev) => ({
      ...prev,
      trainingDialogMode: Training_Dialog_Mode.edit,
      selectedTraining: row,
      isOpenAddTrainingDialog: true
    }));
  };

  const onAttributeAction = (e: any, attr: any) => {
    if (menu) menu.open(e.target);
    setActionTarget(e.target);
    setActionAttribute(attr);
  };

  const closeActionMenus = () => {
    setActionTarget(null);
    setActionAttribute(null);
    if (menu) menu.close();
  };

  const renderActionMenu = () => {
    if (!actionAttribute || !actionTarget) return null;

    // instructor ability
    if (isInstructorViewing && !isSelfView()) {
      return (
        <div>
          <MenuItem onClick={() => onEditTraining(actionAttribute)}>Edit Training</MenuItem>
        </div>
      );
    }

    if (actionAttribute.statusValue === EmployeeTrainingStatus.NeedsEmployeeFeedback)
      return (
        <div>
          <MenuItem
            onClick={() => onFeedbackAction(actionAttribute)}
            disabled={
              employee === null ||
              employee.hasJob === false ||
              (profile && profile.hasApprover === false)
            }
          >
            Provide feedback
          </MenuItem>
          {actionAttribute.trainingCourses && actionAttribute.trainingCourses.length !== 0}
        </div>
      );
  };

  const rowHasAction = (row: any) => {
    return (
      (row.statusValue !== EmployeeTrainingStatus.PendingApproval && isSelfView()) ||
      (isInstructorViewing && !isSelfView())
    );
  };

  const onCloseAddDisabledNotice = (e: any) => {
    setState((prev) => ({ ...prev, showAddDisabledNotice: false }));
  };

  const showAddAction =
    ((!readOnly && !isInstructorViewing) || (isInstructorViewing && !isSelfView())) &&
    data &&
    data.trainings &&
    data.trainings.length > 0;

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid
          item
          xs={12}
          md={12}
          lg={12}
          display={"flex"}
          justifyContent={"space-between"}
          alignItems={"center"}
        >
          <MultipleFilter
            allowMultiple
            options={filters}
            value={state.selectedFilters}
            getItemLabel={(item: any) => item.label}
            placeholder={"Select type"}
            onChange={onFilterTypeChange}
            sx={{
              width: 424
            }}
          />

          {showAddAction && (
            <Button
              onClick={onOpenAddTrainingDialog}
              size='md'
              variant='filled'
              label='Add training'
              startIcon={<AddIcon />}
            />
          )}
        </Grid>
        {isLoading ? (
          <Grid
            item
            xs={12}
            md={12}
            lg={12}
            display={"flex"}
            justifyContent={"center"}
            alignItems={"center"}
          >
            <SectionLoader />
          </Grid>
        ) : (
          <Grid item xs={12} md={12} lg={12}>
            {state.filteredRows.length ? (
              <ProfileTrainingTable
                rows={state.filteredRows}
                theme={theme}
                showDialog={onShowTrainingAttributesDialog}
                sortable={true}
                sortModel={state.sortModel}
                sortingMode='client'
                selectable
                onRowSelect={onRowSelect}
                rowHasAction={rowHasAction}
                rowNoActionIcon={<PendingIcon className='klayo-attrtable_pendingicon' />}
                onRowAction={onAttributeAction}
                readOnly={readOnly}
              />
            ) : (
              <Placeholder
                text={{
                  primary: "Nothing to show yet",
                  secondary: <div>Any mapped training you complete will appear here.</div>
                }}
                icon={<CertificateIcon />}
                sx={{ height: "100vh", maxHeight: "40vh" }}
              />
            )}
          </Grid>
        )}
      </Grid>

      <AttributeActionMenu
        ref={setMenuRef}
        onClose={closeActionMenus}
        onRender={renderActionMenu}
      />

      {state.isTrainingAttributesDialogShow && (
        <ProfileTrainingCompetenciesDialog
          onClose={onCloseTrainingAttributesDialog}
          associatedAttributeDetail={state.associatedAttributeDetail}
          theme={theme}
        />
      )}

      <PracticalConfirmationDialog
        open={state.isPracticalConfirmationDialogShow}
        onClose={onClosePracticalConfirmationDialog}
        onConfirm={onConfirmPractical}
        practical={state.selectedPractical}
        theme={theme}
        isLoading={state.isConfirmPracticalLoading}
        isInstructorViewing={isInstructorViewing ?? false}
        isSelfView={isSelfView()}
      />
      <AddEditEmployeeTrainingDialog
        theme={theme}
        isOpen={state.isOpenAddTrainingDialog}
        onClose={onCloseAddTrainingDialog}
        employee={employee}
        isInstructorViewing={!!isInstructorViewing}
        onLoadTrainings={() => reloadData()}
        mode={state.trainingDialogMode}
        training={state.selectedTraining}
        user={user}
        trainingOptions={trainingOptions}
        isFetching={isFetching}
        groupId={groupId}
      />

      <Dialog
        open={state.showAddDisabledNotice}
        theme={theme}
        title='Feature not available'
        onClose={onCloseAddDisabledNotice}
        maxWidth='sm'
        actions={[
          {
            label: "Close",
            primary: true,
            onClick: onCloseAddDisabledNotice
          }
        ]}
      >
        You can’t add any trainings because you haven’t been assigned a manager yet. Please contact
        your system administrator for assistance.
      </Dialog>
    </Box>
  );
};
